import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'react-hot-toast';
import { Plus, Search, Eye, RefreshCw } from 'lucide-react';
import axios from 'axios';
import DataTable from '../components/common/DataTable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getStatusColor = (status) => {
  switch (status) {
    case 'CREATED': return 'bg-orange-100 text-orange-800';
    case 'RELEASED': return 'bg-yellow-100 text-yellow-800';
    case 'POSTED': return 'bg-green-100 text-green-800';
    case 'CANCELLED': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const BillingList = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [billingDocs, setBillingDocs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({ status: '', payer: '', sales_order_number: '' });
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    loadMasterData();
    loadBillingDocs();
  // eslint-disable-next-line
  }, []);

  const loadMasterData = async () => {
    try {
      const custRes = await axios.get(`${BACKEND_URL}/api/customers`).catch(() => ({ data: [] }));
      setCustomers(custRes.data || []);
    } catch (error) {
      console.error('Error loading master data:', error);
    }
  };

  const loadBillingDocs = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.payer) params.append('payer', filters.payer);
      if (filters.sales_order_number) params.append('sales_order_number', filters.sales_order_number);

      const response = await axios.get(`${BACKEND_URL}/api/otc/billing-documents?${params.toString()}`);
      setBillingDocs(response.data || []);
    } catch (error) {
      console.error('Error loading billing documents:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to load billing documents: ${errorMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => loadBillingDocs();
  const handleClearFilters = () => setFilters({ status: '', payer: '', sales_order_number: '' });

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.customer_id === customerId);
    return customer?.customer_name || customer?.name || customerId;
  };

  const columns = [
    { key: 'billing_number', header: 'Invoice #', filterable: true, accessor: (d) => d.billing_number,
      render: (d) => <span className="font-mono font-bold text-orange-600">{d.billing_number}</span> },
    { key: 'payer', header: 'Payer', filterable: true,
      accessor: (d) => `${getCustomerName(d.payer)} ${d.payer}`,
      render: (d) => (
        <div>
          <p className="font-medium">{getCustomerName(d.payer)}</p>
          <p className="text-xs text-gray-500">{d.payer}</p>
        </div>
      ) },
    { key: 'sales_order_number', header: 'Sales Order', accessor: (d) => d.sales_order_number || '-',
      render: (d) => <span className="font-mono">{d.sales_order_number || '-'}</span> },
    { key: 'delivery_number', header: 'Delivery', accessor: (d) => d.delivery_number || '-',
      render: (d) => <span className="font-mono">{d.delivery_number || '-'}</span> },
    { key: 'billing_date', header: 'Billing Date', accessor: (d) => d.billing_date?.split('T')[0] },
    { key: 'net_value', header: 'Net Value', align: 'right',
      render: (d) => <span className="font-mono">{d.currency} {d.net_value?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span> },
    { key: 'gross_value', header: 'Gross Value', align: 'right',
      render: (d) => <span className="font-mono font-bold">{d.currency} {d.gross_value?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span> },
    { key: 'status', header: 'Status',
      render: (d) => <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(d.status)}`}>{d.status}</span> },
    { key: 'actions', header: 'Actions', align: 'right',
      render: (d) => (
        <Button size="sm" variant="outline" onClick={() => navigate(`/otc/billing/${d.billing_number}`)} title="View Invoice">
          <Eye className="w-4 h-4" />
        </Button>
      ) },
  ];

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Billing Documents</h1>
            <p className="text-sm text-gray-600 mt-1">Manage customer invoices and billing</p>
          </div>
          {canWrite('billing_document') && (
          <Button onClick={() => navigate('/otc/billing/create')} data-testid="create-billing-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Billing
          </Button>
          )}
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-48">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Status</label>
              <Select value={filters.status || '__ALL__'} onValueChange={(v) => setFilters({...filters, status: v === '__ALL__' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="All Statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__">All Statuses</SelectItem>
                  <SelectItem value="CREATED">Created</SelectItem>
                  <SelectItem value="RELEASED">Released</SelectItem>
                  <SelectItem value="POSTED">Posted</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Payer</label>
              <Select value={filters.payer || '__ALL__'} onValueChange={(v) => setFilters({...filters, payer: v === '__ALL__' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="All Payers" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__">All Payers</SelectItem>
                  {customers.map(c => (
                    <SelectItem key={c.customer_id} value={c.customer_id}>
                      {c.customer_id} - {c.customer_name || c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSearch} data-testid="search-btn">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
            <Button variant="outline" onClick={handleClearFilters}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Clear
            </Button>
          </div>
        </Card>

        {/* Billing Documents Table */}
        <Card className="p-6">
          <DataTable
            testid="billing-table"
            columns={columns}
            data={billingDocs}
            loading={isLoading}
            rowKey={(d) => d.billing_number}
            emptyText="No billing documents found."
            emptyHint="Create a billing document from a delivery to get started."
            rowProps={(d) => ({ 'data-testid': `billing-${d.billing_number}` })}
          />
        </Card>
      </div>
    </Layout>
  );
};

export default BillingList;
